import React, { useState, useEffect } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import CustomerHeader from "../../components/CustomerHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Volume2, Play, Music, Download, Gem, Sparkles, Gift, Crown, Check } from "lucide-react";
import { toast } from "sonner";
import { isSoundEnabled, setSoundEnabled, play, SOUND_KEYS, unlockAudio, playBrandJingle, downloadBrandJingle } from "../../lib/sound";
import NotificationSettings from "../../components/NotificationSettings";
import CustomerTabBar from "../../components/CustomerTabBar";
import { useNavigate } from "react-router-dom";
import { Bell, Palette, Award, Target, Trophy, Heart, Stamp } from "lucide-react";
import { Map as MapIcon, LayoutGrid } from "lucide-react";

const SOUND_LABELS = {
  app_open: "Apertura app", order_confirmed: "Ordine confermato", order_cancelled: "Ordine annullato",
  rider_assigned: "Rider assegnato", rider_incoming: "Rider in arrivo", rider_arrived: "Rider arrivato",
  delivery_completed: "Consegna completata", mission_completed: "Missione completata", level_up: "Livello sbloccato",
  badge: "Badge ottenuto", credits: "Crediti ricevuti", ai_notify: "Notifica assistente", error: "Errore",
  success: "Successo", notification: "Notifica",
};

function StatCard({ testid, icon: Icon, color, bg, border, label, value, hint }) {
  return (
    <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0" style={{ background: bg, border: `1px solid ${border}` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
          <div data-testid={testid} className="text-2xl font-display font-bold" style={{ color }}>{value}</div>
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 mt-2 leading-snug">{hint}</p>
    </div>
  );
}

export default function CustomerProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [level, setLevel] = useState(null);
  const [points, setPoints] = useState(null);
  const [plus, setPlus] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [mapTheme, setMapTheme] = useState(() => localStorage.getItem("fuma_theme") || "world");
  const [homeMode, setHomeMode] = useState(["ai", "smart", "mappa"].includes(user?.home_mode) ? user.home_mode : "ai");
  const [aiPrefs, setAiPrefs] = useState({ enabled: false });
  const [username, setUsername] = useState(user?.username || "");
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [zone, setZone] = useState(user?.zone || "");
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");

  useEffect(() => { api.get("/user/level").then(({ data }) => setLevel(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/fuma-ai/preferences").then(({ data }) => setAiPrefs(data)).catch(() => {}); }, []);
  const toggleAiPrefs = async (v) => { setAiPrefs((p) => ({ ...p, enabled: v })); try { const { data } = await api.put("/fuma-ai/preferences", { enabled: v }); setAiPrefs(data); toast.success(v ? "Personalizzazione Fuma AI attiva" : "Personalizzazione disattivata"); } catch (e) {} };
  useEffect(() => { api.get("/game/rider-run/rewards").then(({ data }) => setRewards(data.rewards || [])).catch(() => {}); }, []);

  const loadPoints = () => api.get("/customer/points").then(({ data }) => setPoints(data)).catch(() => {});
  const loadPlus = () => api.get("/customer/fuma-plus").then(({ data }) => setPlus(data)).catch(() => {});

  useEffect(() => {
    loadPoints(); loadPlus();
    const sess = new URLSearchParams(window.location.search).get("fuma_plus_session");
    if (!sess) return;
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sess}`);
        if (data.payment_status === "paid") {
          clearInterval(iv); toast.success("Fuma+ attivato! 🎉"); loadPlus(); loadPoints();
          window.history.replaceState({}, "", "/app/profilo");
        }
      } catch (e) { /* keep polling */ }
      if (tries >= 15) { clearInterval(iv); loadPlus(); }
    }, 2000);
    return () => clearInterval(iv);
  }, []);

  const subscribePlus = async () => {
    try {
      const { data } = await api.post("/customer/fuma-plus/subscribe", { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancelPlus = async () => {
    try { await api.post("/customer/fuma-plus/cancel"); toast.success("Abbonamento Fuma+ annullato"); loadPlus(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const SOURCE_LABELS = { order: "Ordine", mission: "Missione", giftcard: "Gift card", referral: "Invito" };

  const save = async () => {
    try {
      const { data } = await api.put("/auth/profile", { username, first_name: firstName, last_name: lastName, phone, address, zone });
      setUser(data); toast.success("Profilo aggiornato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveHomeMode = async (m) => {
    setHomeMode(m);
    try { const { data } = await api.put("/auth/profile", { home_mode: m }); setUser(data); toast.success("Schermata iniziale aggiornata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changePassword = async () => {
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      setCurPw(""); setNewPw(""); toast.success("Password aggiornata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] grain pb-24">
      <CustomerHeader />
      <main className="max-w-2xl mx-auto px-4 lg:px-8 py-8 relative z-10">
        <h1 className="font-display font-black text-3xl tracking-tighter mb-6">Il mio profilo</h1>

        <button data-testid="profile-passaporto-banner" onClick={() => navigate("/app/passaporto")}
          className="w-full mb-5 rounded-2xl p-4 border border-[#00E676]/40 bg-gradient-to-r from-[#00E676]/15 to-[#131316] flex items-center gap-3 text-left active:scale-[0.99] transition-transform">
          <div className="h-11 w-11 rounded-2xl bg-[#00E676]/15 flex items-center justify-center shrink-0"><Stamp className="h-6 w-6 text-[#00E676]" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-['Unbounded'] font-bold text-sm">Passaporto Fuma</div>
            <div className="text-xs text-white/55">Colleziona timbri esplorando il territorio</div>
          </div>
          <span className="text-[#00E676] text-sm font-semibold shrink-0">Apri →</span>
        </button>

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-6" data-testid="profile-quicknav">
          {[
            { id: "livelli", label: "Livelli", icon: Award, to: "/app/livelli" },
            { id: "missioni", label: "Missioni", icon: Target, to: "/app/missioni" },
            { id: "rider-run", label: "🛵 Rider Run", icon: Target, to: "/app/rider-run" },
            { id: "rider-skins", label: "🎨 Skin", icon: Palette, to: "/app/rider-run/skins" },
            { id: "classifica", label: "Classifica", icon: Trophy, to: "/app/classifica" },
            { id: "notifiche", label: "Notifiche", icon: Bell, to: "/app/notifiche" },
            { id: "preferiti", label: "Preferiti", icon: Heart, to: "/lista/preferiti" },
            { id: "regala", label: "Invita", icon: Gift, to: "/app/regala" },
          ].map((c) => (
            <button key={c.id} data-testid={`profile-quick-${c.id}`} onClick={() => navigate(c.to)}
              className="flex flex-col items-center justify-center gap-1.5 rounded-2xl bg-[#1C1C1F] border border-white/10 hover:border-[#00E676]/40 py-3 transition-colors active:scale-95">
              <c.icon className="h-5 w-5 text-[#00E676]" />
              <span className="text-[10px] font-medium text-white/75">{c.label}</span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <StatCard testid="stat-credits" icon={Gem} color="#C4B5FD" bg="rgba(139,92,246,0.14)" border="rgba(139,92,246,0.4)" label="💎 Crediti" value={user?.credits ?? 0} hint="Valuta spendibile · 100 crediti = 1€ al checkout" />
          <StatCard testid="stat-xp" icon={Sparkles} color="#7DF9FF" bg="rgba(125,249,255,0.12)" border="rgba(125,249,255,0.4)" label={level?.level_label ? `⭐ XP · ${level.level_label}` : "⭐ XP"} value={level?.xp ?? user?.xp ?? 0} hint="Esperienza di gioco · completa missioni per salire di livello" />
          <StatCard testid="loyalty-points" icon={Gift} color="#FFD700" bg="rgba(255,215,0,0.12)" border="rgba(255,215,0,0.4)" label="🎁 Punti fedeltà" value={points?.balance ?? user?.loyalty_points ?? 0} hint="Premi accumulati con la spesa reale sugli ordini" />
        </div>

        {rewards.length > 0 && (
          <div className="rounded-lg p-5 mb-6 border border-[#FFD700]/40 bg-gradient-to-br from-[#FFD700]/10 to-[#131316]" data-testid="profile-rewards">
            <div className="font-display font-bold text-lg flex items-center gap-2 mb-3">🎟️ Le mie ricompense</div>
            <div className="grid gap-2">
              {rewards.map((r, i) => (
                <div key={i} data-testid={`profile-reward-${r.coupon_code}`} className={`rounded-2xl p-3 border flex items-center justify-between gap-3 ${r.still_valid ? "border-[#FFD700]/40 bg-[#FFD700]/5" : "border-white/10 bg-[#1C1C1F] opacity-60"}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{r.business_name} <span className="text-zinc-500 font-normal">— {r.mission_title}</span></div>
                    <div className="font-mono text-[#FFD700] mt-0.5">{r.coupon_code} <span className="text-zinc-400 text-xs">{r.discount_label}</span>{!r.still_valid && <span className="text-red-400 text-[11px] ml-1">· scaduto</span>}</div>
                  </div>
                  <button data-testid={`profile-reward-copy-${r.coupon_code}`} disabled={!r.still_valid}
                    onClick={() => { try { navigator.clipboard?.writeText(r.coupon_code); } catch (e) {} toast.success("Codice copiato"); }}
                    className="px-3 py-2 rounded-full bg-[#FFD700] text-black text-xs font-semibold shrink-0 disabled:opacity-40">Copia</button>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">Vinti in Fuma Rider Run · usa i codici al checkout.</div>
          </div>
        )}

        <div data-testid="fuma-plus-card" className={`rounded-lg p-5 mb-6 border ${plus?.active ? "border-[#FFD700]/50 bg-gradient-to-br from-[#FFD700]/12 to-[#131316]" : "border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/10 to-[#131316]"}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Crown className={`h-7 w-7 ${plus?.active ? "text-[#FFD700]" : "text-[#00E676]"}`} />
              <div>
                <div className="font-display font-bold text-lg flex items-center gap-2">
                  Fuma+
                  {plus?.active && <span data-testid="fuma-plus-badge" className="text-[10px] bg-[#FFD700] text-black rounded-full px-2 py-0.5 font-bold tracking-wide">ATTIVO</span>}
                </div>
                <div className="text-xs text-zinc-400">{plus?.active ? "Membership premium attiva" : `Membership premium · €${Number(plus?.price_month ?? 4.99).toFixed(2)}/mese`}</div>
              </div>
            </div>
            {plus?.active
              ? <Button data-testid="fuma-plus-cancel" onClick={cancelPlus} variant="outline" className="rounded-full border-white/20 text-sm">Annulla abbonamento</Button>
              : <Button data-testid="fuma-plus-subscribe" onClick={subscribePlus} className="bg-[#00E676] text-black rounded-full text-sm font-semibold">Attiva Fuma+ · €{Number(plus?.price_month ?? 4.99).toFixed(2)}/mese</Button>}
          </div>
          <ul className="mt-4 space-y-1.5">
            {(plus?.benefits || []).map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-300"><Check className="h-4 w-4 text-[#00E676] shrink-0 mt-0.5" /> {b}</li>
            ))}
          </ul>
        </div>

        {points && (
          <div data-testid="points-wallet" className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-display font-bold text-xl flex items-center gap-2"><Gift className="h-5 w-5 text-[#FFD700]" /> Wallet Punti fedeltà</h2>
              {points.is_plus
                ? <span data-testid="points-multiplier" className="text-xs bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40 rounded-full px-2.5 py-0.5 font-semibold">Punti x{points.multiplier}</span>
                : <span className="text-xs text-zinc-500">Con Fuma+ guadagni il doppio</span>}
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div data-testid="points-balance" className="text-2xl font-display font-bold text-[#FFD700]">{points.balance}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Saldo</div></div>
              <div><div className="text-2xl font-display font-bold">{points.earned}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Guadagnati</div></div>
              <div><div className="text-2xl font-display font-bold">{points.spent}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Spesi</div></div>
            </div>
            {points.movements?.length > 0 ? (
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid="points-history">
                {points.movements.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-t border-white/5 pt-1.5">
                    <span className="text-zinc-400 truncate">{SOURCE_LABELS[m.source] || m.source || "Movimento"} · {(m.at || "").slice(0, 10)}</span>
                    <span className={m.delta >= 0 ? "text-[#00E676] font-semibold" : "text-red-400 font-semibold"}>{m.delta >= 0 ? "+" : ""}{m.delta}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-xs text-zinc-500">Nessun movimento ancora. Ordina per iniziare ad accumulare punti.</p>}
          </div>
        )}

        <a href="/app/regala" data-testid="growth-link" className="block rounded-lg p-5 mb-6 border border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/12 to-[#131316] hover:border-[#00E676]/60 transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Gift className="h-6 w-6 text-[#00E676]" />
              <div><div className="font-semibold">Invita amici & Regala gift card</div><div className="text-xs text-zinc-400">Guadagna crediti invitando e regala Fuma c'anduma con una gift card</div></div>
            </div>
            <span className="text-[#00E676] text-sm">Apri →</span>
          </div>
        </a>
        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4">
          <div>
            <Label>Username <span className="text-zinc-500 font-normal">(usato nelle classifiche)</span></Label>
            <Input data-testid="profile-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="es. mario_rossi" className="bg-[#0A0A0C] border-white/10 mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome</Label><Input data-testid="profile-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1.5" /></div>
            <div><Label>Cognome</Label><Input data-testid="profile-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1.5" /></div>
          </div>
          <p className="text-xs text-zinc-500 -mt-1">Nome e cognome vengono usati per le consegne e i dati fiscali/azienda.</p>
          <div><Label>Email</Label><Input value={user?.email} disabled className="bg-[#0A0A0C] border-white/10 mt-1.5 opacity-60" /></div>
          <div><Label>Telefono</Label><Input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1.5" /></div>
          <div><Label>Indirizzo</Label><Input data-testid="profile-address" value={address} onChange={(e) => setAddress(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1.5" /></div>
          <div><Label>Zona</Label><Input data-testid="profile-zone" value={zone} onChange={(e) => setZone(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1.5" /></div>
          <Button data-testid="save-profile" onClick={save} className="bg-[#00E676] text-black rounded-full">Salva modifiche</Button>
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4 mt-6">
          <h2 className="font-display font-bold text-xl">Cambia password</h2>
          <div><Label>Password attuale</Label><Input data-testid="cust-pw-current" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1.5" /></div>
          <div><Label>Nuova password</Label><Input data-testid="cust-pw-new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1.5" /></div>
          <Button data-testid="cust-pw-save" onClick={changePassword} className="bg-[#00E676] text-black rounded-full">Aggiorna password</Button>
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4 mt-6" data-testid="notification-settings-section">
          <h2 className="font-display font-bold text-xl flex items-center gap-2"><Bell className="h-5 w-5 text-[#00E676]" /> Impostazioni notifiche</h2>
          <p className="text-sm text-zinc-400 -mt-1">Scegli come essere avvisato per ogni categoria: Push, Email o notifica In-app. Le promozioni sono disattivate finché non le attivi tu.</p>
          <NotificationSettings />
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4 mt-6" data-testid="home-mode-settings">
          <h2 className="font-display font-bold text-xl flex items-center gap-2"><LayoutGrid className="h-5 w-5 text-[#00E676]" /> Schermata iniziale</h2>
          <p className="text-sm text-zinc-400 -mt-1">Scegli come vuoi aprire l'app: la Mappa Viva o la Home Smart con le sezioni. Puoi cambiare in qualsiasi momento anche dall'interruttore rapido.</p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: "ai", label: "Fuma AI", desc: "Assistente intelligente", icon: Sparkles },
              { k: "mappa", label: "Mappa Viva", desc: "Mappa gamificata del territorio", icon: MapIcon },
              { k: "smart", label: "Home Smart", desc: "Sezioni semplici e immediate", icon: LayoutGrid },
            ].map((t) => (
              <button key={t.k} data-testid={`home-mode-${t.k}`} onClick={() => saveHomeMode(t.k)}
                className={`rounded-xl p-4 border text-left transition-colors ${homeMode === t.k ? "border-[#00E676] bg-[#00E676]/10" : "border-white/10 bg-[#0A0A0C] hover:border-white/25"}`}>
                <t.icon className={`h-5 w-5 mb-2 ${homeMode === t.k ? "text-[#00E676]" : "text-zinc-400"}`} />
                <div className="font-semibold text-sm">{t.label}</div>
                <div className="text-xs text-zinc-500">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4 mt-6" data-testid="fuma-ai-prefs">
          <h2 className="font-display font-bold text-xl flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#00E676]" /> Fuma AI — personalizzazione</h2>
          <p className="text-sm text-zinc-400 -mt-1">Attiva per far ricordare a Fuma AI solo preferenze utili (categorie, fascia di prezzo, zone e interessi) e proporti cose più adatte a te. Non usiamo mai dati sensibili. Puoi disattivarla quando vuoi.</p>
          <div className="flex items-center justify-between rounded-xl bg-[#0A0A0C] border border-white/10 px-4 py-3">
            <span className="text-sm font-medium">Ricorda le mie preferenze</span>
            <Switch data-testid="fuma-prefs-toggle" checked={!!aiPrefs.enabled} onCheckedChange={toggleAiPrefs} />
          </div>
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4 mt-6" data-testid="theme-settings">
          <h2 className="font-display font-bold text-xl flex items-center gap-2"><Palette className="h-5 w-5 text-[#00E676]" /> Tema mappa</h2>
          <p className="text-sm text-zinc-400 -mt-1">Scegli lo stile visivo della mappa nella Home.</p>
          <div className="grid grid-cols-2 gap-3">
            {[{ k: "world", label: "Fuma World", desc: "Verde brand" }, { k: "aurora", label: "Blu / Rosso", desc: "Aurora" }].map((t) => (
              <button key={t.k} data-testid={`theme-opt-${t.k}`} onClick={() => { setMapTheme(t.k); localStorage.setItem("fuma_theme", t.k); }}
                className={`rounded-xl p-4 border text-left transition-colors ${mapTheme === t.k ? "border-[#00E676] bg-[#00E676]/10" : "border-white/10 bg-[#0A0A0C] hover:border-white/25"}`}>
                <div className="font-semibold text-sm">{t.label}</div>
                <div className="text-xs text-zinc-500">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6 space-y-4 mt-6" data-testid="sound-settings">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-xl flex items-center gap-2"><Volume2 className="h-5 w-5 text-[#00E676]" /> Identità sonora</h2>
            <Switch data-testid="sound-toggle" checked={soundOn} onCheckedChange={(v) => { setSoundOn(v); setSoundEnabled(v); if (v) { unlockAudio(); play("success"); } }} />
          </div>
          <p className="text-sm text-zinc-400">Effetti sonori brevi ed eleganti per le azioni dell'app. Tocca un suono per ascoltarlo in anteprima.</p>
          <div className="rounded-lg bg-[#0A0A0C] border border-[#00E676]/20 p-3 flex items-center justify-between flex-wrap gap-2" data-testid="brand-jingle">
            <div className="flex items-center gap-2 text-sm"><Music className="h-4 w-4 text-[#00E676]" /> Logo sonoro del brand (1,5s)</div>
            <div className="flex items-center gap-2">
              <button data-testid="brand-jingle-play" onClick={() => { unlockAudio(); playBrandJingle(); }} className="text-xs bg-white/8 border border-white/15 rounded-full px-3 py-1.5 hover:bg-white/12 flex items-center gap-1"><Play className="h-3.5 w-3.5" /> Ascolta</button>
              <button data-testid="brand-jingle-download" onClick={() => downloadBrandJingle()} className="text-xs bg-[#00E676] text-black rounded-full px-3 py-1.5 flex items-center gap-1 font-medium"><Download className="h-3.5 w-3.5" /> Scarica WAV</button>
            </div>
          </div>
          {soundOn && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {SOUND_KEYS.map((k) => (
                <button key={k} data-testid={`sound-preview-${k}`} onClick={() => { unlockAudio(); play(k); }}
                  className="flex items-center gap-2 text-left text-xs bg-[#0A0A0C] border border-white/10 rounded-lg px-3 py-2 hover:border-[#00E676]/40 transition-colors">
                  <Play className="h-3.5 w-3.5 text-[#00E676] shrink-0" /> <span className="truncate">{SOUND_LABELS[k] || k}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>
      <CustomerTabBar active="profilo" />
    </div>
  );
}
